import { createContext, useCallback, useEffect, useState, type ReactNode } from "react";

export type ThemeName = "clean" | "warm" | "dark" | "earth" | "bold";

const THEME_NAMES: ThemeName[] = ["clean", "warm", "dark", "earth", "bold"];
const STORAGE_KEY = "prime-theme";
const DEFAULT_THEME: ThemeName = "clean";

interface ThemeState {
  theme: ThemeName;
  setTheme: (theme: ThemeName) => void;
  themes: readonly ThemeName[];
}

export const ThemeContext = createContext<ThemeState | null>(null);

function isValidTheme(value: string): value is ThemeName {
  return (THEME_NAMES as readonly string[]).includes(value);
}

function readStoredTheme(): ThemeName {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null && isValidTheme(stored)) {
      return stored;
    }
  } catch {
    // localStorage may be unavailable
  }
  return DEFAULT_THEME;
}

function applyThemeClass(theme: ThemeName): void {
  const body = document.body;
  // Remove all theme classes
  for (const name of THEME_NAMES) {
    body.classList.remove(`theme-${name}`);
  }
  body.classList.add(`theme-${theme}`);
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps): ReactNode {
  const [theme, setThemeState] = useState<ThemeName>(readStoredTheme);

  // Apply theme class on mount and when theme changes
  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  const setTheme = useCallback((newTheme: ThemeName) => {
    setThemeState(newTheme);
    try {
      localStorage.setItem(STORAGE_KEY, newTheme);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEME_NAMES }}>
      {children}
    </ThemeContext.Provider>
  );
}
